import { useContext, useEffect, useState } from 'react';
import Moment from 'react-moment';
import { Trans } from '@lingui/react';
import { useDrag, useDrop } from 'react-dnd-latest';
import { IconDragDrop, IconDragDrop2, IconGripVertical } from '@tabler/icons';
import { ConfirmDialog, ModalDialog, useConfirmModalState, useModalState } from '../../Molecules/Dialogs';
import {
  deleteFormSubmission,
  deleteResource,
  fetchFormSubmissionOfResource,
} from '../../Service/restapi/shareService';
import { file } from '../../Service/FileActions';
import { TransButtonLink } from '../../Atoms/Buttons';
import { EventPortalContext } from '../../Service/Context/EventPortalContext';
import { ShareS } from '../../Service/ShareS';
import FormSubmissionModal from './FormSubmissionModal';
import { ActionIcon, Box, Button, Divider, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { i18n } from '@lingui/core';
import LinkText from '../../Atoms/LinkText';
import { WindowContext } from '../../Service/Context/WindowContext';
import Dot from '../../Atoms/Dot';
import { ResourceStatusIcon } from '../../Molecules/Event/Documents/DocumentResourceBadge';
import { useParams } from 'react-router-dom';

const ResourceItem = ({ resource, index, setResources }) => {
  const { reload } = useContext(EventPortalContext);
  const { id: eventId } = useParams();
  const { showConfirm, toggleConfirm } = useConfirmModalState(false);
  const [deleteCustomerData, setDeleteCustomerData] = useState(false);
  const [formSubmission, setFormSubmission] = useState(undefined);
  const { show, toggle } = useModalState(false);
  const { colorScheme, colors } = useMantineTheme();
  const { md } = useContext(WindowContext);

  const resourceStatus = !formSubmission
    ? 'PUBLISHED'
    : formSubmission && formSubmission.lastSubmitted
    ? 'CONFIRMED'
    : 'SEEN';

  useEffect(() => {
    fetchSubmission();
  }, [resource]);

  function delResource() {
    deleteResource(resource.id)
      .then((resp) => {
        if (resp.ok) {
          reload();
        } else {
          console.error(`could not delete resource ${resource.id}`);
        }
      })
      .catch((error) => {
        console.error({ error });
        console.error(`could not delete resource ${resource.id}`);
      });
  }

  function fetchSubmission() {
    fetchFormSubmissionOfResource(resource.id)
      .then((resp) => {
        if (resp.ok) {
          resp.json().then((formSubmission) => {
            setFormSubmission(formSubmission);
          });
        } else if (resp.status === 404) {
          setFormSubmission(undefined);
          console.info('not submitted yet');
        } else {
          console.error(`could not fetch formSubmission of resource ${resource.id}`);
        }
      })
      .catch((error) => {
        console.error({ error });
        console.error(`could not fetch formSubmission of resource ${resource.id}`);
      });
  }

  function delFormSubmit() {
    deleteFormSubmission(formSubmission.id).then((resp) => {
      if (resp.ok) {
        fetchSubmission();
      }
    });
  }

  function download() {
    file
      .download(`${process.env.REACT_APP_SHARE_SERVICE_URL}/formSubmissions/${formSubmission.id}/pdf`)
      .then(() => {})
      .catch((response) => {});
  }

  const styles = {
    container: {
      flex: 1,
      display: 'flex',
      padding: '5px 10px',
      paddingLeft: 5,
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      // eslint-disable-next-line no-nested-ternary
      backgroundColor: index % 2 === 1 ? (colorScheme === 'dark' ? colors.dark[8] : colors.gray[0]) : 'inherit',
    },
    statePoint: {
      width: 8,
      height: 8,
      backgroundColor: formSubmission && formSubmission.lastSubmitted ? colors.green[3] : colors.orange[3],
      borderRadius: '50%',
    },
  };

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: 'resource',
      canDrop: (item) => item.resource.id !== resource.id && item.resource.shareId === resource.shareId,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: !!monitor.canDrop(),
      }),
      drop(item) {
        ShareS.changeResourceIndex(resource.shareId, item.index, index).then((resouces) => {
          if (resouces) {
            setResources(resouces);
          }
        });
      },
    }),
    [],
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: 'resource',
      item: { resource, index },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
        handlerId: monitor.getHandlerId(),
      }),
    }),
    [],
  );

  return (
    <>
      <div
        style={{
          opacity: isDragging ? 0.4 : 1,
          backgroundColor: index % 2 === 1 ? (colorScheme === 'dark' ? colors.dark[8] : colors.gray[0]) : 'inherit',
        }}
        ref={drop}
      >
        <Group position="apart" p={8}>
          <Group>
            <div style={{ width: 40 }}>
              <ActionIcon ref={drag}>
                {canDrop && <IconDragDrop color="#1D5191" size={isOver ? 28 : 20} />}
                {!canDrop && <IconDragDrop2 size={20} style={{ cursor: 'grab' }} />}
              </ActionIcon>
            </div>

            <Dot colour={formSubmission && formSubmission.lastSubmitted ? colors.green[3] : colors.orange[3]} />

            <Stack spacing={0}>
              <Text>{i18n._(resource.label ? resource.label : `resource.entityType.${resource.entityType}`)}</Text>

              {formSubmission && formSubmission.lastSubmitted && (
                <div>
                  <Text color="dimmed" size="xs">
                    bestätigt <Moment fromNow date={formSubmission.lastSubmitted} />
                  </Text>
                  <ModalDialog
                    size="xl"
                    header={<Trans id="Kundeneingaben verarbeiten" />}
                    body={<FormSubmissionModal formSubmission={formSubmission} closeModal={toggle} eventId={eventId} />}
                    show={show}
                    toggle={toggle}
                  />
                </div>
              )}
            </Stack>
          </Group>
          <div>
            {formSubmission && formSubmission.lastSubmitted && (
              <LinkText size="xs" onClick={toggle}>
                Kundeneingaben ansehen
              </LinkText>
            )}
            {formSubmission && (
              <Button size="xs" variant="subtle" color="red" onClick={() => setDeleteCustomerData(!deleteCustomerData)}>
                Kundeneingaben löschen
              </Button>
            )}
            {!formSubmission && (
              <Button size="xs" variant="subtle" color="red" onClick={toggleConfirm}>
                Freigabe entfernen
              </Button>
            )}
          </div>
        </Group>
      </div>

      <ConfirmDialog
        text="share.resource.delete.confirm"
        subject="share.resource.delete.confirm.head"
        open={showConfirm}
        toggle={toggleConfirm}
        handler={delResource}
      />
      <ConfirmDialog
        text="formSubmission.delete.confirm"
        subject="formSubmission.delete.confirm.head"
        open={deleteCustomerData}
        toggle={() => {
          setDeleteCustomerData(!deleteCustomerData);
        }}
        handler={delFormSubmit}
      />
    </>
  );
};

export default ResourceItem;
